import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import HeroSection from "../../components/heroSection/HeroSection";
import Footer from "../../components/footer/Footer";
import "./FAQDetails.css";

const FAQDetails = () => {
  const navigate = useNavigate();
  const [activeFAQ, setActiveFAQ] = useState(null);
  const [activeTab, setActiveTab] = useState("guest");
  const scrollRef = useRef(null);
  const faqRefs = useRef({});

  const guestFAQData = [
    {
      id: 1,
      question: "How do I buy tickets?",
      answer:
        "Select the event, choose ticket type/quantity, and pay via your preferred payment method.",
      images: ["/images/guest1A.png"],
    },
    {
      id: 2,
      question: "Do I pay extra charges as a guest?",
      answer:
        "Guests do not pay charges on tickets or cash gifts, but a 5% fee applies on clothing and paraphernalia.",
    },
    {
      id: 3,
      question: "Can I get a refund if I can't attend?",
      answer:
        "Yes. Refunds are subject to the event's refund policy set by the host.",
      images: ["/images/guest3A.png"],
    },
    {
      id: 4,
      question: "How do I send a cash gift to the host?",
      answer:
        'Use the "Gift" button on the event page, select your amount, and complete the payment.',
      images: ["/images/guest4A.png"],
    },
    {
      id: 5,
      question: "My payment failed but I was charged, what do I do?",
      answer:
        "The system auto-reverses failed transactions. If you don't receive a refund within 24–48 hours, contact support with your payment reference.",
    },
    {
      id: 6,
      question: "How secure are my payments?",
      answer:
        "All payments are processed through trusted payment gateways, and your details are encrypted for security.",
      images: ["/images/guest6A.png"],
    },
  ];

  const hostFAQData = [
    {
      id: 1,
      question: "How do I create an event?",
      answer:
        'Tap "Create Event," fill in event details (title, date/time, attire, images, tickets, refund policy, etc.), and publish.',
      images: [
        "/images/host1A.png",
        "/images/host1B.png",
        "/images/host1C.png",
      ],
    },
    {
      id: 2,
      question: "Can I edit event after creating it?",
      answer: "Yes, you can edit all event details after it has been created.",
      images: ["/images/host2A.png", "/images/host2B.png"],
    },
    {
      id: 3,
      question: "How do I invite guests to my event?",
      answer:
        "There is an invite link on the event detail page that can be shared with guests on any platform.",
      images: ["/images/host3A.png", "/images/host3B.png"],
    },
    {
      id: 4,
      question: "What fees do I pay as a host?",
      answer:
        "10% on all tickets sold. No charges on attire/clothing or cash gifts.",
    },
    {
      id: 5,
      question: "Can I upload multiple dates and times for one event?",
      answer:
        "Yes, you can set multiple sessions or dates when creating your event.",
      images: ["/images/host5A.png", "/images/host5B.png"],
    },
    {
      id: 6,
      question: "How do I manage ticket sales?",
      answer:
        "From your dashboard, you can track ticket sales and guests lists.",
      images: ["/images/host6A.png"],
    },
    {
      id: 7,
      question: "Can I set a refund policy?",
      answer: "Yes, hosts can specify refund timelines during event setup.",
      images: ["/images/host7A.png"],
    },
    {
      id: 8,
      question: "When do I receive my funds?",
      answer:
        "Payments (Event attire sales, ticket sales, and gifts) are transferred to you after deductions, in your account given to the support team.",
    },
  ];

  const handleNavClick = (page) => {
    navigate(page);
  };

  const handleLogin = () => {
    console.log("Login clicked");
    navigate("/onboarding");
  };

  const handleGetStarted = () => {
    console.log("Get Started clicked");
    navigate("/onboarding");
  };

  const faqData = activeTab === "host" ? hostFAQData : guestFAQData;

  const toggleFAQ = (id) => {
    if (activeFAQ === id) {
      setActiveFAQ(null);
      setTimeout(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTo({
            top: 0,
            behavior: "smooth",
          });
        }
      }, 50);
      return;
    }

    setActiveFAQ(null);

    setTimeout(() => {
      const element = faqRefs.current[id];
      if (element && scrollRef.current) {
        const elementTop = element.offsetTop;
        const scrollContainer = scrollRef.current;
        const offset = 20;

        scrollContainer.scrollTo({
          top: elementTop - offset,
          behavior: "smooth",
        });
      }

      setTimeout(() => {
        setActiveFAQ(id);
      }, 100);
    }, 50);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setActiveFAQ(null);
  };

  const getTitle = () => {
    return "Frequently Asked Questions";
  };

  const handleBack = () => {
    navigate(-1);
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <div className="faq_details_container">
      <HeroSection
        title="Frequently Asked Questions"
        description="Get answers to the most frequently asked questions about using our platform. From creating an event to managing your guest list, we've got you covered."
        showDecorative={true}
        variant="about"
        activeNav="about"
        onNavClick={handleNavClick}
        onLoginClick={handleLogin}
        onGetStartedClick={handleGetStarted}
      />

      <div className="faq_details_header"></div>

      <div className="faq_details_tabs">
        <button
          className={`faq_details_tab ${
            activeTab === "guest" ? "faq_details_tab_active" : ""
          }`}
          onClick={() => handleTabChange("guest")}
        >
          Guest FAQ
        </button>
        <button
          className={`faq_details_tab ${
            activeTab === "host" ? "faq_details_tab_active" : ""
          }`}
          onClick={() => handleTabChange("host")}
        >
          Host FAQ
        </button>
      </div>

      <div className="faq_details_scroll_view" ref={scrollRef}>
        {faqData.map((item) => (
          <div
            key={item.id}
            className="faq_details_item"
            ref={(el) => {
              if (el) faqRefs.current[item.id] = el;
            }}
          >
            <button
              className="faq_details_question"
              onClick={() => toggleFAQ(item.id)}
            >
              <span
                className={`faq_details_question_text ${
                  activeFAQ === item.id ? "faq_details_active_question" : ""
                }`}
              >
                {item.question}
              </span>
              <svg
                className="faq_details_chevron"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{
                  transform:
                    activeFAQ === item.id ? "rotate(180deg)" : "rotate(0deg)",
                }}
              >
                <path
                  d="M6 9L12 15L18 9"
                  stroke="#666"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {activeFAQ === item.id && (
              <div className="faq_details_answer">
                <p className="faq_details_answer_text">{item.answer}</p>

                {item.images && item.images.length > 0 && (
                  <div className="faq_details_images_container">
                    {item.images.map((image, index) => (
                      <div key={index} className="faq_details_image_wrapper">
                        {item.images.length > 1 && (
                          <div className="faq_details_image_indicator">
                            <span className="faq_details_image_indicator_text">
                              {index + 1}/{item.images.length}
                            </span>
                          </div>
                        )}
                        <img
                          src={image}
                          alt={`FAQ ${item.id} - Image ${index + 1}`}
                          className="faq_details_image"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      <Footer />
    </div>
  );
};

export default FAQDetails;
